/**
 * Centralized UI copy for EN/ES. Mirrors the same "resolve locale centrally,
 * fall back to English on anything missing" discipline the backend's
 * notification content catalogs use (src/notifications/localization) — kept
 * as a separate small dictionary here since this is UI chrome, not
 * notification content.
 */

export type Locale = 'en' | 'es';

type Dict = Record<string, { en: string; es?: string }>;

const STRINGS: Dict = {
  // The brand/product name is never translated, in either language.
  brand: { en: 'Useless Notifications', es: 'Useless Notifications' },
  headline: {
    en: "Notifications you don't need, about things you don't care about.",
    es: 'Notificaciones que no necesitas sobre cosas que no te importan.',
  },
  subcopy: {
    en: 'Trivial facts. Random updates. Weather in places you’ve never been. Absolutely no value.',
    es: 'Datos triviales. Actualizaciones al azar. El clima en lugares donde nunca estuviste. Ningún valor agregado.',
  },
  howOften: { en: 'How often?', es: '¿Con qué frecuencia?' },
  freqOccasionallyTitle: { en: 'Occasionally', es: 'De vez en cuando' },
  freqOccasionallySub: { en: 'A little bit', es: 'Solo algunas' },
  freqRegularlyTitle: { en: 'Regularly', es: 'Regularmente' },
  freqRegularlySub: { en: 'A nice amount', es: 'Una buena cantidad' },
  freqFrequentlyTitle: { en: 'Frequently', es: 'Frecuentemente' },
  freqFrequentlySub: { en: 'Bring it on', es: 'Sin piedad' },
  // Short enough to stay on one line in the frequency grid at any width the
  // other three cards' labels fit at — see src/frontend/styles.css .freq-title.
  freqRegretTitle: { en: "I'll regret this", es: 'Me voy a arrepentir' },
  freqRegretSub: { en: 'No mercy', es: 'Sin compasión' },
  enableButton: { en: 'Enable Useless Notifications', es: 'Activar Useless Notifications' },
  enableCaption: {
    en: 'Requires browser notifications. No account needed.',
    es: 'Requiere permitir notificaciones. No necesitas cuenta.',
  },

  enablingButton: { en: 'Enabling…', es: 'Activando…' },

  activeMessage: { en: 'Useless notifications are enabled.', es: 'Las notificaciones inútiles están activadas.' },
  saveFrequencyButton: { en: 'Save frequency', es: 'Guardar frecuencia' },
  savingButton: { en: 'Saving…', es: 'Guardando…' },
  disableButton: { en: 'Disable', es: 'Desactivar' },
  disablingButton: { en: 'Disabling…', es: 'Desactivando…' },

  disabledMessage: {
    en: 'Disabled. As if nothing happened. Which is, to be fair, our whole thing.',
    es: 'Desactivado. Como si nada hubiera pasado. Que, para ser justos, es nuestro objetivo.',
  },
  enableAgainButton: { en: 'Enable again', es: 'Activar de nuevo' },

  deniedMessage: { en: 'Notifications are blocked in your browser.', es: 'Las notificaciones están bloqueadas en tu navegador.' },
  deniedNote: {
    en: "You'll need to allow notifications for this site in your browser settings, then reload this page.",
    es: 'Necesitás permitir las notificaciones para este sitio en la configuración de tu navegador y luego recargar la página.',
  },

  unsupportedMessage: { en: "This browser can't receive push notifications.", es: 'Este navegador no puede recibir notificaciones push.' },
  unsupportedNote: {
    en: 'Try a recent version of Chrome, Firefox, Edge, or Safari.',
    es: 'Probá con una versión reciente de Chrome, Firefox, Edge o Safari.',
  },

  iosInstallMessage: { en: 'One more step on iPhone.', es: 'Un paso más en iPhone.' },
  iosInstallStep1: {
    en: 'Tap the Share icon in Safari’s toolbar.',
    es: 'Tocá el ícono de Compartir en la barra de Safari.',
  },
  iosInstallStep2: {
    en: 'Choose "Add to Home Screen," then open Useless Notifications from there. Yes, really.',
    es: 'Elegí "Agregar a inicio" y después abrí Useless Notifications desde ahí. Sí, en serio.',
  },

  errorMessage: { en: 'Something went wrong. Which is, admittedly, on-brand.', es: 'Algo salió mal. Lo cual, hay que admitirlo, va con la marca.' },
  tryAgainButton: { en: 'Try again', es: 'Intentar de nuevo' },

  scrollHint: { en: 'See what you’ll get', es: 'Ver qué vas a recibir' },

  samplesTitle: { en: "What you'll get", es: 'Qué vas a recibir' },
  sample1Title: { en: 'Good news', es: 'Buenas noticias' },
  sample1Body: { en: 'Nothing happened.', es: 'No pasó nada.' },
  sample2Title: { en: 'Current weather in Reykjavik', es: 'Clima actual en Reikiavik' },
  sample2Body: { en: "8°C. You weren't there.", es: '8°C. Vos no estás ahí, pero por las dudas.' },
  sample3Title: { en: 'It is currently 03:17 in Ulaanbaatar', es: 'Son las 03:17 en Ulán Bator' },
  sample3Body: { en: 'This information was not necessary.', es: 'Esta información no era necesaria.' },

  faqTitle: { en: 'Questions you might have', es: 'Preguntas que podrías tener' },
  faqQ1: { en: 'Can I choose topics?', es: '¿Puedo elegir los temas?' },
  faqA1: { en: 'No.', es: 'No.' },
  faqQ2: { en: 'How many will I get?', es: '¿Cuántas voy a recibir?' },
  faqA2: { en: 'Some. Approximately.', es: 'Algunas. Aproximadamente.' },
  faqQ3: { en: 'Will you wake me up?', es: '¿Me van a despertar?' },
  faqA3: {
    en: "No — 08:00–22:00 your time. We're useless, not rude.",
    es: 'No — de 08:00 a 22:00, hora local. Somos inútiles, no maleducados.',
  },
  faqQ4: { en: 'What do you keep?', es: '¿Qué información guardan?' },
  faqA4: {
    en: 'We keep a notification subscription, your language, frequency, timezone, and roughly where in the world you are. No account, email, or IP.',
    es: 'Guardamos una suscripción de notificaciones, tu idioma, frecuencia, zona horaria y más o menos en qué parte del mundo estás. Sin cuenta, email ni IP.',
  },
  faqQ5: { en: 'How do I stop?', es: '¿Cómo lo detengo?' },
  faqA5: { en: 'Come back and press Disable.', es: 'Volvé y presioná Desactivar.' },

  footer: { en: 'Useless Notifications. Nothing else to see.', es: 'Useless Notifications. No hay nada más que ver.' },
  creditPrefix: { en: 'Brought to you by', es: 'Hecho por' },

  detailBack: { en: 'Back to uselessness', es: 'Volver a la inutilidad' },
};

export function t(key: keyof typeof STRINGS, locale: Locale): string {
  const entry = STRINGS[key];
  if (!entry) return '';
  if (locale === 'es' && entry.es) return entry.es;
  return entry.en;
}

const SUPPORTED_LOCALES: Locale[] = ['en', 'es'];

export function detectInitialLocale(stored: string | null): Locale {
  if (stored === 'en' || stored === 'es') return stored;
  const nav = typeof navigator !== 'undefined' ? navigator.language : 'en';
  const short = nav.slice(0, 2).toLowerCase();
  return (SUPPORTED_LOCALES as string[]).includes(short) ? (short as Locale) : 'en';
}
